import { getCsrfToken } from "@/integrations/php/client";

export type ReportRange = "7d" | "30d" | "90d" | "all";

export interface EmailStatistics {
  summary: {
    sent: number;
    failed: number;
    opened: number;
    clicked: number;
    delivery_rate: number;
    open_rate: number;
    click_rate: number;
    avg_open_hours: number;
  };
  daily: Array<{ date: string; sent: number; opened: number; clicked: number }>;
  click_types: Array<{ name: string; total: number }>;
  partners: Array<{
    id: string;
    name: string;
    sent: number;
    opened: number;
    clicked: number;
    open_rate: number;
  }>;
  events: Array<{
    tracking_id: string;
    lead_id: string | null;
    lead_name: string | null;
    lead_service: string | null;
    partner_id: string | null;
    partner_name: string | null;
    email: string | null;
    email_type: "partner" | "admin" | "lead";
    sent_at: string | null;
    opened_at: string | null;
    clicks: number;
    click_types: string[];
  }>;
  pagination: { page: number; per_page: number; total: number; pages: number };
}

export interface SmsStatistics {
  totalSent: number;
  delivered: number;
  failed: number;
  pending: number;
  reads: number;
  viewRate: number;
  avgReadTimeHours: number;
  unread: number;
  previousPeriodSent: number;
  trend: number;
  isPositive: boolean;
  daily: Array<{ date: string; sent: number; reads: number }>;
  byPartner: Array<{ partnerId: string; partnerName: string; sent: number; reads: number }>;
  statusBreakdown: { sent: number; delivered: number; failed: number; pending: number };
}

export type PartnerMiniStatistics = Record<string, { views: number; callClicks: number; mapsClicks: number }>;

const adminHeaders = async () => ({ "X-CSRF-Token": await getCsrfToken() });

export async function fetchEmailStatistics(input: {
  range: ReportRange;
  page: number;
  perPage?: number;
  search?: string;
}): Promise<EmailStatistics> {
  const params = new URLSearchParams({
    range: input.range,
    page: String(input.page),
    per_page: String(input.perPage ?? 25),
  });
  if (input.search?.trim()) params.set("search", input.search.trim());

  const response = await fetch(`/api/admin/email-statistics.php?${params}`, {
    credentials: "include",
    headers: await adminHeaders(),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok || !body.data) {
    throw new Error(body?.error?.message || "Δεν ήταν δυνατή η φόρτωση των email reports.");
  }
  return body.data as EmailStatistics;
}

export async function fetchSmsStatistics(days: 7 | 14 | 30): Promise<SmsStatistics> {
  const response = await fetch(`/api/get-sms-stats.php?days=${days}`, {
    credentials: "include",
    headers: await adminHeaders(),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error("Δεν ήταν δυνατή η φόρτωση των SMS reports.");
  return body as SmsStatistics;
}

export async function exportEmailStatisticsCsv(range: ReportRange, search = ""): Promise<void> {
  const params = new URLSearchParams({ range, export: "csv" });
  if (search.trim()) params.set("search", search.trim());
  const response = await fetch(`/api/admin/email-statistics.php?${params}`, {
    credentials: "include",
    headers: await adminHeaders(),
  });
  if (!response.ok) throw new Error("Η εξαγωγή email CSV απέτυχε.");
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `diathermiki-email-statistics-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export async function fetchPartnerMiniStatistics(): Promise<PartnerMiniStatistics> {
  const response = await fetch("/api/get-partner-mini-stats.php", {
    credentials: "include",
    headers: await adminHeaders(),
  });
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error("Δεν ήταν δυνατή η φόρτωση των mini-page statistics.");
  const result: PartnerMiniStatistics = {};
  const items: unknown[] = Array.isArray(body?.items) ? body.items : [];
  items.forEach((rawItem) => {
    if (typeof rawItem !== "object" || rawItem === null) return;
    const item = rawItem as Record<string, unknown>;
    if (!item?.partnerId) return;
    result[String(item.partnerId)] = {
      views: Number(item.views || 0),
      callClicks: Number(item.callClicks || 0),
      mapsClicks: Number(item.mapsClicks || 0),
    };
  });
  return result;
}
